<template>
  <div>
    <div class="page-header">
      <div class="page-icon">🖨️</div>
      <h2>打印设置</h2>
    </div>

    <el-row :gutter="20">
      <!-- 左：表单 -->
      <el-col :xs="24" :md="12">
        <el-card class="box-card">
          <el-form :model="form" label-width="100px">
            <el-form-item label="店名">
              <el-input v-model="form.storeName" placeholder="小票顶部店名" maxlength="20" show-word-limit></el-input>
            </el-form-item>

            <el-form-item label="销售单标题">
              <el-input v-model="form.saleLabel" placeholder="默认：销售单（可改：出货单）" maxlength="10"></el-input>
            </el-form-item>

            <el-form-item label="进货单标题">
              <el-input v-model="form.purchaseLabel" placeholder="默认：进货单（可改：收货单）" maxlength="10"></el-input>
            </el-form-item>

            <el-form-item label="页脚">
              <el-input v-model="form.footer" placeholder="默认：谢谢惠顾" maxlength="30"></el-input>
            </el-form-item>

            <el-form-item label="纸张宽度">
              <el-radio-group v-model="form.paperWidth">
                <el-radio :value="58">58 mm</el-radio>
                <el-radio :value="80">80 mm</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item label="列宽调整">
              <div class="col-width-block">
                <div class="col-row" v-for="k in colKeys" :key="k">
                  <span class="col-label">{{ colLabels[k] }}</span>
                  <el-slider
                    v-model="form.cols[k]"
                    :min="limits[k].min"
                    :max="limits[k].max"
                    :step="1"
                    show-input
                    :show-input-controls="false"
                    size="small"
                    style="flex: 1; margin: 0 12px;"
                  ></el-slider>
                  <span class="col-tip">当前 {{ form.cols[k] }} 字符</span>
                </div>
                <div class="col-tip" style="margin-top: 4px;">表总宽 {{ totalWidth }} 字符（58mm 纸最多 32 字符）</div>
              </div>
            </el-form-item>

            <el-form-item>
              <el-button type="primary" @click="handleSave">保存设置</el-button>
              <el-button type="success" @click="handleTestPrint">测试打印</el-button>
              <el-button type="warning" plain @click="handleClearCache">清除已配对打印机</el-button>
            </el-form-item>
          </el-form>

          <el-alert
            v-if="!webUSPSupported"
            type="error"
            :closable="false"
            title="当前浏览器不支持 WebUSB，请使用 Chrome 或 Edge 浏览器"
            show-icon
            style="margin-top: 16px;"
          ></el-alert>

          <el-alert
            v-else
            type="info"
            :closable="false"
            title="首次打印会弹出浏览器授权框选择 USB 设备；Windows 用户需先用 Zadig 把驱动替换为 WinUSB 才能连接热敏打印机"
            show-icon
            style="margin-top: 16px;"
          ></el-alert>
        </el-card>
      </el-col>

      <!-- 右：实时预览 -->
      <el-col :xs="24" :md="12">
        <el-card class="box-card">
          <template #header>
            <div class="preview-header">
              <span>实时预览（样例数据）</span>
              <el-radio-group v-model="previewType" size="small">
                <el-radio-button value="sale">{{ form.saleLabel }}</el-radio-button>
                <el-radio-button value="purchase">{{ form.purchaseLabel }}</el-radio-button>
              </el-radio-group>
            </div>
          </template>

          <div class="receipt-preview" :style="{ fontSize: form.paperWidth === 80 ? '13px' : '11px' }">
            <div class="r-store">{{ preview.storeName }}</div>
            <div class="r-type">── {{ preview.typeLabel }} ──</div>

            <div class="r-section-title">商品明细</div>
            <div class="r-sep">{{ preview.sep }}</div>
            <div class="r-row r-head">
              <span :style="{ width: ch(preview.cols.name) }">种类</span>
              <span :style="{ width: ch(preview.cols.qty) }" class="ta-r">数量</span>
              <span :style="{ width: ch(preview.cols.price) }" class="ta-r">单价</span>
              <span :style="{ width: ch(preview.cols.sub) }" class="ta-r">小计</span>
            </div>
            <div class="r-sep">{{ preview.sep }}</div>

            <div v-for="(it, idx) in preview.items" :key="idx" class="r-row">
              <span :style="{ width: ch(preview.cols.name) }" :title="it.nameFull">{{ it.name }}</span>
              <span :style="{ width: ch(preview.cols.qty) }" class="ta-r">{{ it.quantity }}</span>
              <span :style="{ width: ch(preview.cols.price) }" class="ta-r">{{ it.price }}</span>
              <span :style="{ width: ch(preview.cols.sub) }" class="ta-r">{{ it.subtotal }}</span>
            </div>

            <div class="r-sep">{{ preview.sep }}</div>

            <div class="r-summary">数量汇总: {{ preview.totalQuantity }} 件</div>
            <div class="r-summary">小计汇总: {{ preview.total }} 元</div>

            <div v-if="preview.date" class="r-date">{{ preview.date }}</div>
            <div class="r-footer">{{ preview.footer }}</div>
            <div class="r-footer">　</div>
            <div class="r-footer">　</div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { receiptSettings } from '@/utils/receiptSettings';
import { isWebUSPSupported, printToThermalPrinter, clearPrinterCache } from '@/utils/thermalPrinter';
import { buildTestBytes, buildReceiptPreview } from '@/utils/escpos';

const webUSPSupported = ref(false);
const previewType = ref('sale');

const colKeys = ['name', 'qty', 'price', 'sub'];
const colLabels = { name: '种类', qty: '数量', price: '单价', sub: '小计' };
const limits = receiptSettings.getColumnLimits();

const form = ref({
  storeName: '',
  saleLabel: '销售单',
  purchaseLabel: '进货单',
  footer: '谢谢惠顾',
  paperWidth: 80,
  cols: { name: 12, qty: 4, price: 7, sub: 7 }
});

const SAMPLE_ITEMS = [
  { name: '可口可乐', quantity: 2, price: 3.0, subtotal: 6.0 },
  { name: '雪碧', quantity: 1, price: 3.5, subtotal: 3.5 },
  { name: '百事可乐（特价）', quantity: 3, price: 6.5, subtotal: 19.5 }
];

const preview = computed(() => buildReceiptPreview({
  storeName: form.value.storeName || '店铺名',
  type: previewType.value,
  typeLabel: previewType.value === 'purchase' ? form.value.purchaseLabel : form.value.saleLabel,
  date: new Date().toLocaleString('zh-CN'),
  items: SAMPLE_ITEMS,
  total: SAMPLE_ITEMS.reduce((a, x) => a + x.subtotal, 0),
  totalQuantity: SAMPLE_ITEMS.reduce((a, x) => a + x.quantity, 0),
  footer: form.value.footer || '谢谢惠顾',
  cols: form.value.cols
}));

const totalWidth = computed(() =>
  form.value.cols.name + form.value.cols.qty + form.value.cols.price + form.value.cols.sub + 4
);

function ch(width) { return `${width}ch`; }

onMounted(() => {
  webUSPSupported.value = isWebUSPSupported();
  form.value = {
    storeName: receiptSettings.getStoreName(),
    saleLabel: receiptSettings.getSaleLabel(),
    purchaseLabel: receiptSettings.getPurchaseLabel(),
    footer: receiptSettings.getFooter(),
    paperWidth: receiptSettings.getPaperWidth(),
    cols: receiptSettings.getColumnWidths()
  };
});

function handleSave() {
  receiptSettings.setStoreName(form.value.storeName.trim());
  receiptSettings.setSaleLabel(form.value.saleLabel.trim());
  receiptSettings.setPurchaseLabel(form.value.purchaseLabel.trim());
  receiptSettings.setFooter(form.value.footer.trim());
  receiptSettings.setPaperWidth(form.value.paperWidth);
  receiptSettings.setColumnWidths(form.value.cols);
  ElMessage.success('设置已保存');
}

async function handleTestPrint() {
  if (!webUSPSupported.value) {
    ElMessage.error('当前浏览器不支持 WebUSB，请使用 Chrome 或 Edge 浏览器');
    return;
  }
  try {
    const bytes = buildTestBytes();
    const dev = await printToThermalPrinter(bytes);
    ElMessage.success('已发送到打印机: ' + (dev.productName || '热敏打印机'));
  } catch (e) {
    if (e.name === 'NotFoundError') return;
    console.error('[测试打印] 失败:', e);
    ElMessage.error('测试打印失败：' + e.message);
  }
}

async function handleClearCache() {
  try {
    await ElMessageBox.confirm(
      '清除后下次打印需要重新选择 USB 设备，确定吗？',
      '提示',
      { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
    );
    clearPrinterCache();
    ElMessage.success('已清除，下次打印会重新弹出授权框');
  } catch (e) {
    /* 用户取消 */
  }
}
</script>

<style scoped>
.box-card {
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  border: 1px solid var(--border-lighter);
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.col-width-block {
  width: 100%;
}
.col-row {
  display: flex;
  align-items: center;
  margin: 4px 0;
}
.col-label {
  width: 40px;
  font-size: 13px;
}
.col-tip {
  font-size: 12px;
  color: var(--text-tertiary, #909399);
}

.receipt-preview {
  font-family: 'Consolas', 'Courier New', monospace;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  padding: 16px 12px;
  color: #333;
  line-height: 1.5;
  white-space: nowrap;
  overflow-x: auto;
}
.r-store { text-align: center; font-size: 1.5em; font-weight: 700; margin-bottom: 4px; }
.r-type { text-align: center; margin-bottom: 12px; }
.r-section-title { text-align: center; margin: 4px 0 2px; }
.r-sep { color: #909399; margin: 2px 0; }
.r-row { display: flex; }
.r-head { font-weight: 700; }
.r-row span {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ta-r { text-align: right; }
.r-summary { text-align: right; font-weight: 600; margin: 2px 0; }
.r-date, .r-footer { text-align: center; }
</style>